/**
 * ペルソナの入力フォーム。登録と編集で同じものを使う。
 *
 * エラーの出しかたは 2 系統ある。
 * - 送る前に画面で分かるもの（未入力・長すぎ）は送らずにその場で出す
 * - サーバーが返す userErrors は field に紐づけて、該当の入力欄の下に出す。
 *   field が無いものはフォームの先頭にまとめて出す
 */

import { useState, type FormEvent, type ReactNode } from "react";
import {
  Button,
  Card,
  type CreatePersonaMutation,
} from "visumo-data-agent-web-shared";

/**
 * 画面が扱う業務エラー。mutation の結果型から取る（スキーマの UserError を
 * 別途輸入すると、operation が実際に選んでいるフィールドとずれうる）。
 */
export type PersonaUserError =
  CreatePersonaMutation["createPersona"]["userErrors"][number];

export type PersonaFormValues = {
  name: string;
  description: string;
  attributes: string[];
  barriers: string[];
  questions: string[];
};

export const NAME_MAX = 60;

export function splitLines(value: string): string[] {
  return value
    .split("\n")
    .map((line) => line.trim())
    .filter((line) => line !== "");
}

export function validate(values: {
  name: string;
  description: string;
}): Record<string, string> {
  const errors: Record<string, string> = {};
  if (values.name.trim() === "") {
    errors["name"] = "名前を入力してください。";
  } else if (values.name.trim().length > NAME_MAX) {
    errors["name"] = `名前は ${NAME_MAX} 文字までです。`;
  }
  if (values.description.trim() === "") {
    errors["description"] = "説明を入力してください。";
  }
  return errors;
}

function errorsFor(
  field: string,
  userErrors: readonly PersonaUserError[],
): string[] {
  return userErrors
    .filter((error) => (error.field ?? []).includes(field))
    .map((error) => error.message);
}

function generalErrors(
  userErrors: readonly PersonaUserError[],
): PersonaUserError[] {
  return userErrors.filter((error) => (error.field ?? []).length === 0);
}

export function PersonaForm({
  mode,
  initial,
  userErrors,
  submitting,
  notice,
  onSubmit,
  onCancel,
}: {
  mode: "create" | "edit";
  initial: PersonaFormValues;
  userErrors: readonly PersonaUserError[];
  submitting: boolean;
  notice?: ReactNode;
  onSubmit: (values: PersonaFormValues) => void;
  onCancel: () => void;
}) {
  const [name, setName] = useState(initial.name);
  const [description, setDescription] = useState(initial.description);
  const [attributes, setAttributes] = useState(initial.attributes.join("\n"));
  const [barriers, setBarriers] = useState(initial.barriers.join("\n"));
  const [questions, setQuestions] = useState(initial.questions.join("\n"));
  const [localErrors, setLocalErrors] = useState<Record<string, string>>({});

  const handleSubmit = (event: FormEvent): void => {
    event.preventDefault();
    const errors = validate({ name, description });
    setLocalErrors(errors);
    if (Object.keys(errors).length > 0) {
      return;
    }
    onSubmit({
      name: name.trim(),
      description: description.trim(),
      attributes: splitLines(attributes),
      barriers: splitLines(barriers),
      questions: splitLines(questions),
    });
  };

  const fieldError = (field: string): string[] => {
    const local = localErrors[field];
    return local === undefined
      ? errorsFor(field, userErrors)
      : [local, ...errorsFor(field, userErrors)];
  };

  const general = generalErrors(userErrors);

  return (
    <form onSubmit={handleSubmit} noValidate>
      <Card title={mode === "create" ? "新しいペルソナ" : "ペルソナを編集"}>
        {notice}
        {general.length === 0 ? null : (
          <div className="vd-notice vd-notice--error" role="alert">
            {general.map((error) => (
              <p key={`${error.code}-${error.message}`} style={{ margin: 0 }}>
                {error.message}
              </p>
            ))}
          </div>
        )}

        <div className="vd-stack" style={{ marginTop: 12 }}>
          <div className="vd-field">
            <label htmlFor="persona-name">名前</label>
            <span className="vd-field-hint">
              どんな人かが一目で分かる短い呼び名（{NAME_MAX} 文字まで）
            </span>
            <input
              id="persona-name"
              className="vd-input"
              value={name}
              aria-invalid={fieldError("name").length > 0}
              onChange={(event) => setName(event.target.value)}
            />
            {fieldError("name").map((message) => (
              <span key={message} className="vd-field-error" role="alert">
                {message}
              </span>
            ))}
          </div>

          <div className="vd-field">
            <label htmlFor="persona-description">説明</label>
            <span className="vd-field-hint">
              この人が何を気にして商品を見にくるのか
            </span>
            <textarea
              id="persona-description"
              className="vd-textarea"
              value={description}
              aria-invalid={fieldError("description").length > 0}
              onChange={(event) => setDescription(event.target.value)}
            />
            {fieldError("description").map((message) => (
              <span key={message} className="vd-field-error" role="alert">
                {message}
              </span>
            ))}
          </div>

          <div className="vd-field">
            <label htmlFor="persona-attributes">想定する属性</label>
            <span className="vd-field-hint">
              1 行に 1 つ。自由記述で、採点の判定には使いません
            </span>
            <textarea
              id="persona-attributes"
              className="vd-textarea"
              value={attributes}
              onChange={(event) => setAttributes(event.target.value)}
            />
          </div>

          <div className="vd-field">
            <label htmlFor="persona-barriers">買う前のためらい</label>
            <span className="vd-field-hint">1 行に 1 つ</span>
            <textarea
              id="persona-barriers"
              className="vd-textarea"
              value={barriers}
              onChange={(event) => setBarriers(event.target.value)}
            />
          </div>

          {mode === "create" ? (
            <div className="vd-field">
              <label htmlFor="persona-questions">知りたいこと</label>
              <span className="vd-field-hint">
                1 行に 1 つ。例: サイズ感はどうか
              </span>
              <textarea
                id="persona-questions"
                className="vd-textarea"
                value={questions}
                onChange={(event) => setQuestions(event.target.value)}
              />
            </div>
          ) : (
            <p className="vd-card-note" style={{ margin: 0 }}>
              「知りたいこと」の追加・無効化・注記・削除は、このすぐ下の区画で 1
              件ずつ扱います。
            </p>
          )}

          <div className="vd-row">
            <Button type="submit" variant="primary" disabled={submitting}>
              {submitting ? "保存しています" : "保存する"}
            </Button>
            <Button type="button" onClick={onCancel} disabled={submitting}>
              やめる
            </Button>
          </div>
        </div>
      </Card>
    </form>
  );
}
